import {
  ProtocolCardChain,
  ProtocolCardIdentity,
} from '@/components/admin/protocoles/protocol-card';
import { countLabel } from '@/lib/experience-labels';
import type { ProtocolCard, VersionedRef } from '@matter_extractor/shared';

/**
 * La page protocole, en vignette particulière (refonte modulaire §9.1, §9.5 ;
 * maquette `Timeline.dc.html` ; issue #57).
 *
 * The protocol page is drawn in ITS PLACE in the line, like the others, and it
 * is the one page the eye has to tell apart at a glance: it holds a
 * conversation and not blocks, it cannot be deleted, and its content is chosen
 * rather than typed. So it wears the one mark no other page wears - « une
 * bordure bleu nuit pleine », where the selected page wears a navy RING set off
 * from its frame (§9.1). Two different signs, deliberately: a page can be both.
 *
 * ## Nothing of a protocol is redrawn here
 *
 * §9.5: « le sélecteur lit la liste réelle des protocoles et dessine la carte de
 * §7.1 depuis ce que la carte du protocole porte déjà - sa description, ses
 * étapes dans l'ordre, #48 - : rien n'est redessiné de ce côté ». At the centre
 * of the scene that is literally `ProtocolCardIdentity` and `ProtocolCardChain`,
 * the components `/admin/protocoles` draws its library with.
 *
 * The `neighbour` variant is the one exception and it is a MINIATURE, not a
 * second card: 200 × 125 has room for a name, a count and three step tabs of
 * nine pixels, and the real chain - which carries a 16 px node per step plus a
 * warning line per retired criterion - would overflow it silently. The mockup
 * draws exactly this miniature, and it names the same three steps the folded
 * chain would: the first two, an ellipsis, the last.
 *
 * ## « Aucune conversation n'y est simulée »
 *
 * §9.5, in as many words. The vignette says « Protocole X · n étapes » or
 * « Sans protocole », and jouer une session d'essai passe par un lien en
 * `développement`. So there is no reader here and no bubbles: the reader plays
 * a page of BLOCKS, and this page has none.
 */

export interface ProtocolVignetteProps {
  /** The reference the page stores, or null for « sans protocole » (§9.5). */
  readonly reference: VersionedRef | null;
  /** That reference resolved against the library, or null when it resolves to nothing. */
  readonly card: ProtocolCard | null;
  /** Whether the library has been READ - nothing is claimed before it (§4). */
  readonly resolved: boolean;
  readonly variant: 'centre' | 'neighbour';
}

/** One step of the miniature: its rank, a middot, its name - `ChainNode` shrunk. */
function MiniStep({ index, name }: { index: number; name: string }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1 overflow-hidden rounded border border-gray-200 bg-white px-1 text-[10px] leading-3">
      <span className="shrink-0 font-semibold text-gray-600 tabular-nums">{index + 1}</span>
      <span className="shrink-0 text-gray-400">·</span>
      <span className="truncate text-gray-800">{name}</span>
    </span>
  );
}

/** The first two steps, a vertical ellipsis, the last - the fold of §7.1, shrunk. */
function MiniChain({ card }: { card: ProtocolCard }) {
  const steps = card.steps;
  const folded = steps.length > 3;
  const shown = folded ? steps.slice(0, 2) : steps;
  const last = steps[steps.length - 1];

  return (
    <span className="mt-0.5 flex flex-col items-start gap-0.5 overflow-hidden">
      {shown.map((step, index) => (
        <MiniStep key={step.id} index={index} name={step.name} />
      ))}
      {folded && last !== undefined ? (
        <>
          {/* The CHARACTER and not its numeric entity: the charte's mechanical
              guard reads a hash followed by hex digits as a raw colour. */}
          <span aria-hidden="true" className="pl-1 text-gray-400 text-caption leading-3">
            ⋮
          </span>
          <MiniStep index={steps.length - 1} name={last.name} />
        </>
      ) : null}
    </span>
  );
}

/** What the vignette SAYS when there is nothing to draw a chain from (§9.5). */
function emptyWording(reference: VersionedRef | null, resolved: boolean): string {
  if (reference === null) return 'Sans protocole';
  return resolved ? 'Protocole introuvable' : 'Protocole…';
}

export function ProtocolVignette({ reference, card, resolved, variant }: ProtocolVignetteProps) {
  const centre = variant === 'centre';

  return (
    <div className="flex size-full flex-col overflow-hidden rounded-md border-2 border-bleu-nuit bg-bleu-nuit-tint p-2">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border border-gray-200 bg-white p-2">
        {card === null ? (
          <p
            className={`text-gray-600 ${centre ? 'text-small' : 'text-[11px] leading-4'}`}
          >{`${emptyWording(reference, resolved)} : la page se saute en session.`}</p>
        ) : centre ? (
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
            <ProtocolCardIdentity card={card} />
            <p className="text-gray-600 text-small">
              {`${countLabel(card.steps.length, 'étape')} · ${countLabel(card.jumpCount, 'saut')}`}
            </p>
            <ProtocolCardChain card={card} />
          </div>
        ) : (
          <span className="flex min-h-0 flex-col overflow-hidden">
            <span className="truncate font-semibold text-bleu-nuit text-caption leading-4">
              {card.name}
            </span>
            <span className="text-[10px] text-gray-600 leading-3">
              {`Protocole · ${countLabel(card.steps.length, 'étape')}`}
            </span>
            <MiniChain card={card} />
          </span>
        )}
      </div>
    </div>
  );
}
